import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { RedirectIfAuthed } from "@/components/RedirectIfAuthed";
import { Shell } from "@/components/Shell";
import { Button, Card, ErrorBanner, Field, Input, PasswordInput } from "@/components/ui-kit";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/signup")({
  component: SignupPage,
});

function SignupPage() {
  const { signupBorrower, session, initializing } = useAuth();
  const nav = useNavigate();
  const [stage, setStage] = useState<"form" | "check-email">("form");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // If signup auto-creates a session (email confirmation disabled), go to dashboard.
  useEffect(() => {
    if (session?.kind === "borrower") nav({ to: "/borrower" });
    if (session?.kind === "admin") nav({ to: "/admin" });
  }, [session, nav]);

  if (initializing || session) {
    return (
      <Shell>
        <RedirectIfAuthed />
      </Shell>
    );
  }

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError("Please enter your full name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return setError("Please enter a valid email.");
    if (phone.replace(/\D/g, "").length < 10) return setError("Please enter a valid phone number.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    if (!acceptedTerms) {
      return setError("Please accept the Terms & Conditions and Privacy Policy to continue.");
    }
    setLoading(true);
    try {
      await signupBorrower({ name: name.trim(), email: email.trim(), phone: phone.trim(), password });
      // If a session is now active, the effect above will navigate.
      // Otherwise show the check-email confirmation card.
      setStage("check-email");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Signup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="mx-auto max-w-md px-6 py-16">
        <Card>
          {stage === "form" ? (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Create account</div>
              <h1 className="font-display text-3xl mt-1">Create your account</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Basic info now. After signup you can complete KYC anytime from your dashboard.
              </p>

              <div className="mt-6 space-y-4">
                <ErrorBanner message={error} />
                <Field label="Full name">
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ada Okafor" autoComplete="name" />
                </Field>
                <Field label="Email">
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
                </Field>
                <Field label="Phone">
                  <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+234 801 234 5678" autoComplete="tel" />
                </Field>
                <Field label="Password" hint="At least 8 characters">
                  <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                </Field>
                <Field label="Confirm password">
                  <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
                </Field>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 accent-[var(--primary)]"
                  />
                  <span className="text-muted-foreground">
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" className="text-gold hover:underline">
                      Terms &amp; Conditions
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" target="_blank" className="text-gold hover:underline">
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
                <Button variant="gold" loading={loading} onClick={submit} className="w-full">
                  Create account
                </Button>
                <p className="text-sm text-center text-muted-foreground">
                  Already a member?{" "}
                  <Link to="/login" className="text-gold hover:underline">Sign in</Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Check your inbox</div>
              <h1 className="font-display text-3xl mt-1">Confirm your email</h1>
              <p className="text-sm text-muted-foreground mt-2">
                We've sent a confirmation link to <span className="text-gold">{email}</span>. Click the link to activate your account, then sign in.
              </p>
              <div className="mt-6 space-y-3">
                <Button variant="gold" className="w-full" asChild>
                  <Link to="/login">Go to sign in</Link>
                </Button>
                <button
                  onClick={() => setStage("form")}
                  className="block w-full text-xs text-muted-foreground hover:text-gold"
                >
                  ← Use a different email
                </button>
              </div>
            </>
          )}
        </Card>
      </div>
    </Shell>
  );
}
